import { DATA_BASE_URL, verifyCanonicalJsonArtifact } from './liveOddsVnextContract.js'


const POINTER_SCHEMA = 'abet.board.r2.pointer.v1'
const RELEASE_SCHEMA = 'abet.board.r2.release.v1'
const OBJECT_KEY = /^(?:releases\/[a-f0-9]{64}|shards\/[a-z0-9]+(?:-[a-z0-9]+)*\/[a-f0-9]{64})\.json$/
const SAFE_SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const DATA_ORIGIN = new URL(DATA_BASE_URL).origin
const POINTER_URL = `${DATA_BASE_URL}/r2_release_pointer.json`


function record(value, label) {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new TypeError(`${label} must be an object`)
  }
  return value
}

function text(value, label, pattern = null) {
  if (typeof value !== 'string' || value.length === 0 || (pattern && !pattern.test(value))) {
    throw new TypeError(`${label} is invalid`)
  }
  return value
}

function count(value, label) {
  if (!Number.isSafeInteger(value) || value < 0) throw new TypeError(`${label} is invalid`)
  return value
}

function timestamp(value, label) {
  text(value, label)
  if (!Number.isFinite(Date.parse(value))) throw new TypeError(`${label} is invalid`)
  return value
}

function sameInstant(left, right) {
  const leftValue = Date.parse(left)
  const rightValue = Date.parse(right)
  return Number.isFinite(leftValue) && leftValue === rightValue
}

function objectUrl(key) {
  text(key, 'object key', OBJECT_KEY)
  const url = new URL(`/objects/${key}`, DATA_ORIGIN)
  if (url.protocol !== 'https:' || url.origin !== DATA_ORIGIN) throw new TypeError('object URL is untrusted')
  return url.href
}

async function fetchJson(url, fetchImpl, signal, optional = false) {
  const parsed = new URL(url)
  if (parsed.protocol !== 'https:' || parsed.origin !== DATA_ORIGIN) throw new TypeError('R2 URL is untrusted')
  const response = await fetchImpl(parsed.href, { cache: 'no-store', signal })
  if (optional && response?.status === 404) return null
  if (!response || response.ok !== true || typeof response.json !== 'function') {
    throw new Error(`R2 request failed (HTTP ${response?.status ?? 'unavailable'})`)
  }
  return response.json()
}

function validatePointer(value) {
  const pointer = record(value, 'R2 pointer')
  if (pointer.schema_version !== POINTER_SCHEMA) throw new TypeError('unsupported R2 pointer schema')
  text(pointer.release_id, 'R2 pointer release_id', /^[a-f0-9]{64}$/)
  timestamp(pointer.captured_utc, 'R2 pointer captured_utc')
  text(pointer.manifest_key, 'R2 manifest key', OBJECT_KEY)
  text(pointer.manifest_sha256, 'R2 manifest SHA-256', /^[a-f0-9]{64}$/)
  count(pointer.manifest_canonical_bytes, 'R2 manifest canonical bytes')
  if (!Array.isArray(pointer.categories)) throw new TypeError('R2 pointer categories must be an array')
  const seen = new Set()
  pointer.categories.forEach((item, index) => {
    record(item, `R2 pointer category ${index}`)
    text(item.slug, `R2 pointer category ${index} slug`, SAFE_SLUG)
    text(item.category, `R2 pointer category ${index} name`)
    count(item.row_count, `R2 pointer category ${index} row_count`)
    if (seen.has(item.slug)) throw new TypeError(`duplicate R2 pointer category: ${item.slug}`)
    seen.add(item.slug)
  })
  if (!pointer.manifest_key.endsWith(`/${pointer.manifest_sha256}.json`)) {
    throw new TypeError('R2 manifest key does not match its SHA-256')
  }
  return pointer
}

function validatePartition(value, slug, index) {
  const part = record(value, `partition ${index}`)
  count(part.ordinal, `partition ${index} ordinal`)
  count(part.row_count, `partition ${index} row_count`)
  count(part.canonical_bytes, `partition ${index} canonical_bytes`)
  text(part.sha256, `partition ${index} SHA-256`, /^[a-f0-9]{64}$/)
  text(part.object_key, `partition ${index} object key`, OBJECT_KEY)
  if (!part.object_key.startsWith(`shards/${slug}/`) || !part.object_key.endsWith(`/${part.sha256}.json`)) {
    throw new TypeError(`partition ${index} key does not match category/hash`)
  }
  return part
}

function validateRelease(value, pointer) {
  const release = record(value, 'R2 release')
  if (release.schema_version !== RELEASE_SCHEMA) throw new TypeError('unsupported R2 release schema')
  if (release.release_id !== pointer.release_id) throw new TypeError('R2 release identity mismatch')
  timestamp(release.captured_utc, 'R2 release captured_utc')
  if (Date.parse(release.captured_utc) !== Date.parse(pointer.captured_utc)) {
    throw new TypeError('R2 capture timestamp mismatch')
  }
  if (!Array.isArray(release.categories)) throw new TypeError('R2 release categories must be an array')
  const seen = new Set()
  release.categories.forEach((category, index) => {
    record(category, `R2 release category ${index}`)
    text(category.slug, `R2 release category ${index} slug`, SAFE_SLUG)
    text(category.category, `R2 release category ${index} name`)
    count(category.row_count, `R2 release category ${index} row_count`)
    count(category.source_canonical_bytes, `R2 release category ${index} source bytes`)
    text(category.source_sha256, `R2 release category ${index} source SHA-256`, /^[a-f0-9]{64}$/)
    if (seen.has(category.slug)) throw new TypeError(`duplicate R2 release category: ${category.slug}`)
    seen.add(category.slug)
    if (!Array.isArray(category.partitions)) {
      throw new TypeError(`R2 release category partitions are invalid: ${category.slug}`)
    }
    if ((category.row_count === 0) !== (category.partitions.length === 0)) {
      throw new TypeError(`R2 release category partition cardinality is invalid: ${category.slug}`)
    }
    const objectKeys = new Set()
    const ordinals = category.partitions.map((part, partIndex) => {
      const descriptor = validatePartition(part, category.slug, partIndex)
      if (objectKeys.has(descriptor.object_key)) {
        throw new TypeError(`duplicate R2 partition object key: ${descriptor.object_key}`)
      }
      objectKeys.add(descriptor.object_key)
      return descriptor.ordinal
    })
    const expected = Array.from({ length: ordinals.length }, (_unused, ordinal) => ordinal)
    if (JSON.stringify([...ordinals].sort((a, b) => a - b)) !== JSON.stringify(expected)) {
      throw new TypeError(`R2 partition ordinals are not contiguous: ${category.slug}`)
    }
  })
  return release
}

async function mapBounded(values, limit, mapper) {
  if (!Number.isSafeInteger(limit) || limit < 1 || limit > 4) throw new TypeError('R2 concurrency must be 1-4')
  const results = new Array(values.length)
  let cursor = 0
  const worker = async () => {
    while (cursor < values.length) {
      const index = cursor++
      results[index] = await mapper(values[index], index)
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, values.length) }, worker))
  return results
}

export async function fetchR2Pointer(fetchImpl = fetch, signal) {
  const value = await fetchJson(POINTER_URL, fetchImpl, signal, true)
  return value === null ? null : validatePointer(value)
}

export async function fetchR2Category({
  pointer,
  categorySlug,
  fetchImpl = fetch,
  digestImpl,
  cryptoImpl = globalThis.crypto,
  signal,
  concurrency = 4,
} = {}) {
  const safePointer = validatePointer(pointer)
  text(categorySlug, 'category slug', SAFE_SLUG)
  const release = validateRelease(
    await fetchJson(objectUrl(safePointer.manifest_key), fetchImpl, signal),
    safePointer,
  )
  await verifyCanonicalJsonArtifact(release, {
    sha256: safePointer.manifest_sha256,
    canonical_bytes: safePointer.manifest_canonical_bytes,
  }, { digestImpl, cryptoImpl })
  const category = release.categories.find((item) => item.slug === categorySlug)
  if (!category) throw new Error(`category is absent from R2 release: ${categorySlug}`)
  const pointerCategory = safePointer.categories.find((item) => item.slug === categorySlug)
  if (!pointerCategory || pointerCategory.category !== category.category
      || pointerCategory.row_count !== category.row_count) {
    throw new Error(`R2 pointer/release category mismatch: ${categorySlug}`)
  }
  const ordered = [...category.partitions].sort((left, right) => left.ordinal - right.ordinal)
  const partitions = await mapBounded(ordered, concurrency, async (descriptor) => {
    const value = record(
      await fetchJson(objectUrl(descriptor.object_key), fetchImpl, signal),
      `R2 partition ${descriptor.ordinal}`,
    )
    if (value.schema_version !== 'abet.board.category-partition.v1') {
      throw new TypeError(`R2 partition schema mismatch: ${descriptor.ordinal}`)
    }
    if (value.category !== categorySlug || !Array.isArray(value.rows)) {
      throw new TypeError(`R2 partition category/rows mismatch: ${descriptor.ordinal}`)
    }
    if (value.rows.length !== descriptor.row_count) throw new Error('R2 partition row count mismatch')
    await verifyCanonicalJsonArtifact(value, descriptor, { digestImpl, cryptoImpl })
    return value
  })
  const rows = partitions.flatMap((part) => part.rows)
  if (rows.length !== category.row_count) throw new Error('R2 selected-category row total mismatch')
  const shard = { category: category.category, slug: category.slug, rows }
  const artifactVerification = await verifyCanonicalJsonArtifact(shard, {
    sha256: category.source_sha256,
    canonical_bytes: category.source_canonical_bytes,
  }, { digestImpl, cryptoImpl })
  return { pointer: safePointer, release, category, shard, rows, artifactVerification }
}

export async function fetchMatchingR2Category({
  pointer,
  releaseManifest,
  categoryManifest,
  categorySlug,
  fetchImpl = fetch,
  digestImpl,
  cryptoImpl = globalThis.crypto,
  signal,
} = {}) {
  if (pointer === null
      || pointer.release_id !== releaseManifest.release_id
      || !sameInstant(pointer.captured_utc, releaseManifest.captured_utc)
      || !pointer.categories.some((item) => item.slug === categorySlug)) return null
  const result = await fetchR2Category({
    pointer,
    categorySlug,
    fetchImpl,
    digestImpl,
    cryptoImpl,
    signal,
  })
  const manifestCategory = categoryManifest.categories.find((item) => item.slug === categorySlug)
  if (!manifestCategory) throw new Error(`category is absent from manifest: ${categorySlug}`)
  if (manifestCategory.n_rows !== result.rows.length
      || manifestCategory.n_rows !== result.category.row_count) {
    throw new Error('R2 category row count mismatch')
  }
  if (manifestCategory.category !== result.shard.category
      || manifestCategory.category !== result.category.category) {
    throw new Error('R2 category name mismatch')
  }
  if (!sameInstant(categoryManifest.captured_utc, releaseManifest.captured_utc)
      || !sameInstant(result.release.captured_utc, releaseManifest.captured_utc)) {
    throw new Error('v1 and R2 releases describe different captures')
  }
  const artifacts = { ...releaseManifest.artifacts }
  for (const category of result.release.categories) {
    artifacts[`cat_${category.slug}.json`] = {
      sha256: category.source_sha256,
      canonical_bytes: category.source_canonical_bytes,
    }
  }
  return {
    ...result,
    releaseManifest: { ...releaseManifest, artifacts },
    manifestCategory,
  }
}
